import { useCallback, useMemo, useState } from 'react';
import { usePreserveSelectedHeaderCheckProps } from 'commons/components/table/hooks';
import { OperatorCustomNotificationRow } from 'modules/settings/pods/settings-operator-custom-notifications/settings-operator-custom-notifications.model';

type SelectedRowsUpdate = React.SetStateAction<OperatorCustomNotificationRow[]>;

export const useOperatorCustomNotificationsSelection = (rows: OperatorCustomNotificationRow[]) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);

    const selectedRows = useMemo(() => {
        const selected = new Set(selectedIds);

        return rows.filter((row) => selected.has(row.id));
    }, [rows, selectedIds]);

    const setSelectedRows = useCallback(
        (update: SelectedRowsUpdate) => {
            setSelectedIds((previousIds) => {
                const previouslySelected = new Set(previousIds);
                const previousRows = rows.filter((row) => previouslySelected.has(row.id));
                const nextRows = typeof update === 'function' ? update(previousRows) : update;

                return nextRows.map((row) => row.id);
            });
        },
        [rows]
    );

    const { selectedRowIds, headerCheckboxProps } = usePreserveSelectedHeaderCheckProps({
        selectedRows,
        data: rows,
        setSelectedRows,
    });

    const handleSelectRow = useCallback((id: string, isSelected: boolean) => {
        setSelectedIds((previousIds) =>
            isSelected ? [...previousIds, id] : previousIds.filter((selectedId) => selectedId !== id)
        );
    }, []);

    const clearSelection = useCallback(() => setSelectedIds([]), []);

    return {
        selectedRows,
        selectedRowIds,
        headerCheckboxProps,
        handleSelectRow,
        clearSelection,
        areAllActive: selectedRows.every((row) => row.active),
        areAllInactive: selectedRows.every((row) => !row.active),
    };
};
